import { h } from 'preact'
import { useEffect, useMemo, useRef, useState } from 'preact/hooks'
import { createChatStore } from '../../ai/chat-store.js'
import { ChatStreamHandler } from '../../ai/chat-stream-handler.js'
import { renderBeaconMarkdown } from '../beacon-markdown.js'
import { getWidgetAiConversationMessages } from '../beacon-api.js'
import { t } from '../beacon-i18n.js'

const STREAM_URL = '/ai/kb_stream'

function TypingIndicator({ label }) {
  return (
    <div class="beacon-typing-indicator">
      <span class="beacon-typing-dots">
        <span class="beacon-typing-dot"></span>
        <span class="beacon-typing-dot"></span>
        <span class="beacon-typing-dot"></span>
      </span>
      {label && <span class="beacon-typing-label">{label}</span>}
    </div>
  )
}

/**
 * AiScreen — "mini" AI chat, reusing the real AI chat's reactive store +
 * AG-UI stream handler (chat-store.js / chat-stream-handler.js) but with a
 * lightweight, standalone bubble rendering (no SubagentPanel/CostBadge,
 * no Mermaid/artifact rendering — see plan "AI képernyő architektúrája").
 *
 * Every stream sends source: 'beacon' so the backend tags the resulting
 * Ai::Conversation as source: 'beacon' (excluded from the full /ai history).
 * This is a completely separate chat from the full /ai page — there is no
 * "open in full view" link out to /ai, since a beacon conversation never
 * appears in the full chat's history anyway.
 *
 * store.aiReadOnly (set by store.openAiConversation, "Korábbi beszélgetések")
 * hides the composer entirely — past conversations are view-only and cannot
 * be continued. Only store.openAiFromHome() (the "AI válaszok" home card)
 * starts a live, writable conversation.
 */
export function AiScreen({ store }) {
  const [input, setInput] = useState('')
  const [sending, setSending] = useState(false)
  const [loadingHistory, setLoadingHistory] = useState(false)
  const scrollRef = useRef(null)
  const readOnly = store.aiReadOnly.value

  const chatStore = useMemo(() => createChatStore(), [])
  const streamHandlerRef = useRef(null)
  if (!streamHandlerRef.current) {
    streamHandlerRef.current = new ChatStreamHandler({
      store: chatStore,
      onDone: (conversationId) => {
        setSending(false)
        if (conversationId) store.setAiConversationId(conversationId)
      },
      onError: () => setSending(false),
    })
  }

  useEffect(() => {
    return () => streamHandlerRef.current?.abort()
  }, [])

  // Reopening a previous conversation ("Korábbi beszélgetések" / HomeScreen
  // preview card) — there's no server-rendered DOM to hydrate from here like
  // the full /ai page, so fetch and hydrate the message history explicitly.
  useEffect(() => {
    const agentId = store.aiConversationId.value
    if (!agentId) return

    let cancelled = false
    setLoadingHistory(true)

    getWidgetAiConversationMessages(agentId)
      .then((data) => {
        if (!cancelled) chatStore.hydrateMessages(data.messages)
      })
      .catch(() => { /* Silently keep the empty state — new messages still work. */ })
      .finally(() => {
        if (!cancelled) setLoadingHistory(false)
      })

    return () => { cancelled = true }
  }, [])

  useEffect(() => {
    const el = scrollRef.current
    if (el) el.scrollTop = el.scrollHeight
  }, [chatStore.messages.value, chatStore.streamingId.value, chatStore.streamBufferSignal.value, chatStore.streamingHasContent.value, chatStore.status.value])

  function send(event) {
    event?.preventDefault?.()
    if (readOnly) return
    const message = input.trim()
    if (!message || sending) return

    setSending(true)
    chatStore.addUserMessage({ content: message })
    // Real feedback tied to the actual AG-UI stream lifecycle: set immediately
    // so "Gondolkodom..." shows from the moment the request goes out. Stays visible
    // until RUN_FINISHED (AiScreen hides the in-flight bubble and keeps TypingIndicator
    // while streamingId is set) — never cleared early on first text delta.
    chatStore.setStatus('start', t('support.beacon.ai.thinking'))
    setInput('')

    const { userId, tenantId, personId } = store.identity
    streamHandlerRef.current.startStream(
      STREAM_URL,
      message,
      [],
      [],
      { 'X-Conversation-Id': store.aiConversationId.value },
      userId,
      tenantId,
      personId,
      { source: 'beacon' }
    )
  }

  const messages           = chatStore.messages.value
  const streamingId        = chatStore.streamingId.value
  const streamingHasContent = chatStore.streamingHasContent.value
  const streamBuffer       = chatStore.streamBufferSignal.value
  const status             = chatStore.status.value
  const thinking = status?.code !== 'error' && !streamingHasContent && (!!streamingId || !!status)

  return (
    <div class="beacon-ai-screen">
      <div class="beacon-ai-messages" ref={scrollRef}>
        {loadingHistory && (
          <div class="beacon-ai-empty">{t('support.beacon.ai.history_loading')}</div>
        )}

        {!loadingHistory && messages.length === 0 && !streamingId && (
          <div class="beacon-ai-empty">
            <i class="bi bi-stars"></i>
            <p>{t('support.beacon.ai.empty')}</p>
          </div>
        )}

        {messages.map((msg) => {
          if (msg.type === 'user') {
            return (
              <div key={msg.id} class="beacon-ai-bubble beacon-ai-bubble-user">
                {msg.content}
              </div>
            )
          }
          if (msg.id === streamingId) {
            if (!streamingHasContent) return null
            const html = renderBeaconMarkdown(streamBuffer || msg.content)
            return (
              <div key={msg.id} class="beacon-ai-bubble beacon-ai-bubble-ai" dangerouslySetInnerHTML={{ __html: html }} />
            )
          }
          const html = renderBeaconMarkdown(msg.content)
          return (
            <div key={msg.id} class="beacon-ai-bubble beacon-ai-bubble-ai" dangerouslySetInnerHTML={{ __html: html }} />
          )
        })}

        {thinking && (
          <TypingIndicator label={t('support.beacon.ai.thinking')} />
        )}
        {status?.code === 'error' && (
          <div class="beacon-ai-error">
            <i class="bi bi-exclamation-triangle-fill"></i> {t('support.beacon.ai.error')}
          </div>
        )}
      </div>

      {!readOnly && (
        <form class="beacon-ai-composer" onSubmit={send}>
          <input
            type="text"
            class="beacon-ai-input"
            placeholder={t('support.beacon.ai.placeholder')}
            value={input}
            onInput={(e) => setInput(e.currentTarget.value)}
            disabled={sending}
          />
          <button type="submit" class="beacon-ai-send-btn" disabled={sending || !input.trim()} aria-label={t('support.beacon.ai.send_aria')}>
            <i class="bi bi-send-fill"></i>
          </button>
        </form>
      )}
    </div>
  )
}
